/** @jsxImportSource @emotion/react */
"use client";

import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import type { LSFontFamily } from "@packages/sdk";
import {
  COOKIE_KEY_FONT_STYLE,
  COOKIE_KEY_LETTER_SPACING,
  COOKIE_KEY_PREFERRED_THEME,
  COOKIE_KEY_WORD_SPACING,
  LEGACY_FONT_STYLE_KEY,
  LOCAL_STORAGE_KEY_HLW_FONT_STYLE,
  LOCAL_STORAGE_KEY_HLW_LETTER_SPACING,
  LOCAL_STORAGE_KEY_HLW_WORD_SPACING,
  LOCAL_STORAGE_KEY_PREFERRED_THEME,
  useLegacyLocalState,
} from "@packages/sdk";
import type {
  Dispatch,
  PropsWithChildren,
  ReactNode,
  RefObject,
  SetStateAction,
} from "react";
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import type { HallowElement } from "../../src/types/hallowElement.types";
import type { HallowElementProps } from "../../src/types/hallowElementProps.types";
import type { WithAs } from "../../src/types/withAs.types";
import { determineElementFromAsChild } from "../../src/utils/determineElementFromAsChild";
import type { Theme, ThemeType } from "./theme";
import { themes } from "./theme";

const Element: HallowElement = "div";

type ThemeProviderProps = WithAs<
  HallowElementProps<typeof Element>,
  "body" | "div"
> & {
  children: ReactNode;
  initialTheme?: Theme;
  className?: string;
  fontStyle?: LSFontFamily;
  letterSpacing?: boolean;
  wordSpacing?: boolean;
  forceTheme?: Theme;
  themeCookie?: string;
};

type EmotionThemeContextType = {
  currentTheme: Theme;
  renderedTheme: Extract<Theme, "light" | "dark">;
  switchTheme: Dispatch<SetStateAction<Theme>>;
  fontStyle: LSFontFamily;
  setFontStyle: Dispatch<SetStateAction<LSFontFamily>>;
  letterSpacing: boolean;
  setLetterSpacing: Dispatch<SetStateAction<boolean>>;
  wordSpacing: boolean;
  setWordSpacing: Dispatch<SetStateAction<boolean>>;
  themeContainerRef: RefObject<HTMLDivElement | null>;
};

const EmotionThemeContext = createContext<EmotionThemeContextType>({
  currentTheme: "match",
  renderedTheme: "light",
  switchTheme: () => {},
  fontStyle: "normal",
  setFontStyle: () => {},
  letterSpacing: false,
  setLetterSpacing: () => {},
  wordSpacing: false,
  setWordSpacing: () => {},
  themeContainerRef: null as unknown as RefObject<HTMLDivElement | null>,
});

const ContextProviders = ({
  children,
  theme,
  ...props
}: PropsWithChildren<EmotionThemeContextType & { theme: ThemeType }>) => {
  return (
    <EmotionThemeContext.Provider value={props}>
      <EmotionThemeProvider theme={theme}>{children}</EmotionThemeProvider>
    </EmotionThemeContext.Provider>
  );
};

/**
 * ThemeProvider component that provides the Emotion theme to the application
 * It handles theme switching and system preference detection
 */
export const ThemeProvider = forwardRef<HTMLDivElement, ThemeProviderProps>(
  (
    {
      children,
      className = "",
      as = Element,
      initialTheme: serverTheme = "match",
      fontStyle: serverFontStyle,
      letterSpacing: serverLetterSpacing,
      wordSpacing: serverWordSpacing,
      forceTheme,
      themeCookie = COOKIE_KEY_PREFERRED_THEME,
    },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild: false,
      hallowElement: as,
    });

    const [currentTheme, setCurrentTheme] = useLegacyLocalState<Theme>({
      key: themeCookie,
      legacyLocalStorageKey: LOCAL_STORAGE_KEY_PREFERRED_THEME,
      defaultValue: "match",
      headerValue: serverTheme,
      domain: "hallow.com",
    });
    const [fontStyle, setFontStyle] = useLegacyLocalState<LSFontFamily>({
      key: COOKIE_KEY_FONT_STYLE,
      legacyLocalStorageKey: LOCAL_STORAGE_KEY_HLW_FONT_STYLE,
      defaultValue: "normal",
      headerValue: serverFontStyle,
      domain: "hallow.com",
    });
    const [letterSpacing, setLetterSpacing] = useLegacyLocalState<boolean>({
      key: COOKIE_KEY_LETTER_SPACING,
      legacyLocalStorageKey: LOCAL_STORAGE_KEY_HLW_LETTER_SPACING,
      defaultValue: false,
      headerValue: serverLetterSpacing,
      domain: "hallow.com",
    });
    const [wordSpacing, setWordSpacing] = useLegacyLocalState<boolean>({
      key: COOKIE_KEY_WORD_SPACING,
      legacyLocalStorageKey: LOCAL_STORAGE_KEY_HLW_WORD_SPACING,
      defaultValue: false,
      headerValue: serverWordSpacing,
      domain: "hallow.com",
    });
    const [renderedTheme, setRenderedTheme] = useState<
      Extract<Theme, "light" | "dark">
    >(() => {
      const targetTheme = forceTheme ?? serverTheme;
      if (targetTheme === "match") return "light"; // assume light on server
      return targetTheme;
    });
    const [theme, setTheme] = useState(() => {
      const targetTheme = forceTheme ?? serverTheme;
      return targetTheme === "dark"
        ? themes.dark[fontStyle]
        : themes.light[fontStyle]; // assume light on server, will be fixed on client
    });
    const themeContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const targetTheme = forceTheme ?? currentTheme;
      if (targetTheme === "match" && typeof window !== "undefined") {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

        const handleChange = (e: MediaQueryListEvent) => {
          setTheme(
            e.matches ? themes.dark[fontStyle] : themes.light[fontStyle],
          );
          setRenderedTheme(e.matches ? "dark" : "light");
        };

        mediaQuery.addEventListener("change", handleChange);

        setTheme(
          mediaQuery.matches ? themes.dark[fontStyle] : themes.light[fontStyle],
        );
        setRenderedTheme(mediaQuery.matches ? "dark" : "light");

        return () => {
          mediaQuery.removeEventListener("change", handleChange);
        };
      } else if (targetTheme === "dark") {
        setTheme(themes.dark[fontStyle]);
        setRenderedTheme("dark");
      } else if (targetTheme === "light") {
        setTheme(themes.light[fontStyle]);
        setRenderedTheme("light");
      }
    }, [currentTheme, forceTheme, fontStyle]);

    useEffect(() => {
      if (typeof localStorage !== "undefined") {
        const legacyV1Value = localStorage.getItem(LEGACY_FONT_STYLE_KEY);

        if (legacyV1Value !== null && serverFontStyle === null) {
          setFontStyle(
            legacyV1Value === "0"
              ? "normal"
              : legacyV1Value === "1"
                ? "serif"
                : "sans_serif",
          );
        }
      }
    }, []);

    if (as === null) {
      return (
        <ContextProviders
          theme={theme}
          {...{
            currentTheme: forceTheme ?? currentTheme,
            renderedTheme,
            switchTheme: setCurrentTheme,
            fontStyle,
            setFontStyle,
            letterSpacing,
            setLetterSpacing,
            wordSpacing,
            setWordSpacing,
            themeContainerRef,
          }}
        >
          {children}
        </ContextProviders>
      );
    }

    return (
      <ContextProviders
        theme={theme}
        {...{
          currentTheme: forceTheme ?? currentTheme,
          renderedTheme,
          switchTheme: setCurrentTheme,
          fontStyle,
          setFontStyle,
          letterSpacing,
          setLetterSpacing,
          wordSpacing,
          setWordSpacing,
          themeContainerRef,
        }}
      >
        <DeterminedElement
          css={{
            color: theme.semanticColors.primary,
            backgroundColor: theme.semanticColors.background,
            // CSS custom properties for server component CSS Modules
            "--sc-primary": theme.semanticColors.primary,
            "--sc-background": theme.semanticColors.background,
            "--sc-accent-highest": theme.semanticColors.accentHighest,
            "--sc-accent-medium": theme.semanticColors.accentMedium,
            "--sc-neutrals-high": theme.semanticColors.neutralsHigh,
            "--sc-neutrals-higher": theme.semanticColors.neutralsHigher,
            "--sc-neutrals-lower": theme.semanticColors.neutralsLower,
            "--sc-neutrals-lowest": theme.semanticColors.neutralsLowest,
            "--sc-neutrals-med-high": theme.semanticColors.neutralsMedHigh,
            "--sc-neutrals-medium": theme.semanticColors.neutralsMedium,
            "--sc-neutrals-very-low": theme.semanticColors.neutralsVeryLow,
            "--sc-error": theme.semanticColors.errorError,
            "--sc-success": theme.semanticColors.successSuccess,
            "--sc-secondary": theme.semanticColors.secondary,
            "--sc-accent-med-high": theme.semanticColors.accentMedHigh,
            "--sc-error-medium": theme.semanticColors.errorMedium,
            "--sc-error-med-high": theme.semanticColors.errorMedHigh,
            "--sc-higher-contrast-med": theme.semanticColors.higherContrastMed,
          }}
          className={className}
          ref={(el) => {
            themeContainerRef.current = el;
            if (ref) {
              if (typeof ref === "function") ref(el);
              else ref.current = el;
            }
          }}
        >
          {children}
        </DeterminedElement>
      </ContextProviders>
    );
  },
);

ThemeProvider.displayName = "EmotionThemeProvider";

/**
 * A hook to get the current theme and a function to switch themes
 */
export const useTheme = () => {
  return useContext(EmotionThemeContext);
};
